import React, { useState, useEffect } from 'react';
import { HelpingHand as PrayingHands, Heart, Home } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Database } from '../lib/database.types';
import { PrayerRequestModal } from '../components/PrayerRequestModal';
import { useNavigate } from 'react-router-dom';

type PrayerRequest = Database['public']['Tables']['prayer_requests']['Row'];

export default function PrayerRequests() {
  const [session, setSession] = useState<any>(null);
  const [requests, setRequests] = useState<PrayerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPrayerModalOpen, setIsPrayerModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserRequests(session.user.id);
      } else {
        fetchPublicRequests();
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchUserRequests(session.user.id);
      } else {
        fetchPublicRequests();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchUserRequests(userId: string) {
    try {
      const { data, error } = await supabase
        .from('prayer_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Erro ao buscar pedidos de oração:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchPublicRequests() {
    try {
      const { data, error } = await supabase
        .from('prayer_requests')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Erro ao buscar pedidos de oração:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handlePrayerRequest(name: string, request: string, isPublic: boolean, isAnonymous: boolean) {
    if (!session) return;

    try {
      const { error } = await supabase.from('prayer_requests').insert([
        {
          name: isAnonymous ? 'Anônimo' : name,
          request,
          is_public: isPublic,
          is_anonymous: isAnonymous,
          user_id: session.user.id,
        },
      ]);

      if (error) throw error;

      // Atualizar a lista de pedidos
      await fetchUserRequests(session.user.id);
    } catch (error) {
      console.error('Erro ao salvar pedido de oração:', error);
      alert('Erro ao salvar o pedido. Por favor, tente novamente.');
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <PrayingHands className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Pedidos de Oração</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      {session ? (
        <div className="space-y-6">
          {requests.length > 0 ? (
            <>
              <div className="flex justify-end">
                <button
                  onClick={() => setIsPrayerModalOpen(true)}
                  className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  Novo Pedido
                </button>
              </div>
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-brand-teal">{request.name}</h3>
                    <span className="text-sm text-gray-500">
                      {new Date(request.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-gray-600">{request.request}</p>
                  <div className="mt-2 flex gap-2">
                    {request.is_public && (
                      <span className="inline-block text-sm text-brand-teal bg-brand-light px-2 py-1 rounded">
                        Pedido Público
                      </span>
                    )}
                    {request.is_anonymous && (
                      <span className="inline-block text-sm text-brand-teal bg-brand-light px-2 py-1 rounded">
                        Anônimo
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <Heart className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-brand-teal mb-2">
                Compartilhe Seu Pedido de Oração
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Nossa equipe de intercessão estará orando por você com todo amor e dedicação.
              </p>
              <button
                onClick={() => setIsPrayerModalOpen(true)}
                className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors"
              >
                Fazer Pedido
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Heart className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-teal mb-2">
            Compartilhe Seu Pedido de Oração
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Faça login para compartilhar suas necessidades com nossa equipe de intercessão.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors"
          >
            Fazer Login
          </button>
        </div>
      )}

      <PrayerRequestModal
        isOpen={isPrayerModalOpen}
        onClose={() => setIsPrayerModalOpen(false)}
      />
    </div>
  );
}